import {inject, Injectable, signal} from '@angular/core';
import { environment } from '../../environments/environment';
import {Router} from "@angular/router";
import { ISbDataSourceEntry, StoryblokService } from '@seven1/angular-storyblok/api';

export type TariffType = 'single' | 'family' | undefined;
export type TariffSize = 'basic' | 'comfort' | 'premium' | undefined;
export interface TariffMappingEntry {
  key: string;
  name: string;
  type: string;
  size: string;
}
export interface TariffMapping {
  single: {
    basic: TariffMappingEntry;
    comfort: TariffMappingEntry;
    premium: TariffMappingEntry;
  };
  family: {
    basic: TariffMappingEntry;
    comfort: TariffMappingEntry;
    premium: TariffMappingEntry;
  };
}

export const TARIFF_MAPPING: TariffMapping = {
  single: {
    basic: {
      key: 'sywa-01',
      name: 'SaferYou Single Basis',
      type: 'Single',
      size: 'Basis',
    },
    comfort: {
      key: 'sywa-02',
      name: 'SaferYou Single Komfort',
      type: 'Single',
      size: 'Komfort',
    },
    premium: {
      key: 'sywa-03',
      name: 'SaferYou Single Premium',
      type: 'Single',
      size: 'Premium',
    },
  },
  family: {
    basic: {
      key: 'sywa-04',
      name: 'SaferYou Familie Basis',
      type: 'Familie',
      size: 'Basis',
    },
    comfort: {
      key: 'sywa-05',
      name: 'SaferYou Familie Komfort',
      type: 'Familie',
      size: 'Komfort',
    },
    premium: {
      key: 'sywa-06',
      name: 'SaferYou Familie Premium',
      type: 'Familie',
      size: 'Premium',
    },
  },
};

@Injectable({
  providedIn: 'root',
})
export class TariffStore {
  public router = inject(Router);

  public readonly slug = 'tariff-options';
  public readonly singleSlug = 'tariff-pricing-single';
  public readonly familySlug = 'tariff-pricing-family';

  public isTariffRoute$ = signal(environment.tariff.routes.includes(this.router.url));

  public tariff$ = signal<TariffType>('single');
  public size$ = signal<TariffSize>('comfort');
  public tariffs$ = signal<ISbDataSourceEntry[] | null>([]);
  public tariffPricesSingle$ = signal<ISbDataSourceEntry[] | null>([]);
  public tariffPricesFamily$ = signal<ISbDataSourceEntry[] | null>([]);

  constructor(private cms: StoryblokService) {}

  public async init(): Promise<void> {
    this.tariffs$.set(await this.cms.getDatasourceEntries(this.slug));
    this.tariffPricesSingle$.set(await this.cms.getDatasourceEntries(this.singleSlug));
    this.tariffPricesFamily$.set(await this.cms.getDatasourceEntries(this.familySlug));
  }

  set tariff(val: TariffType) {
    this.tariff$.set(val);
  }
  get tariff(): TariffType {
    return this.tariff$();
  }

  set size(val: TariffSize) {
    this.size$.set(val);
  }
  get size(): TariffSize {
    return this.size$();
  }

  public getPrice(tariff: TariffType, size: TariffSize): string {
    const defaultPrice = '--,-- €';
    switch (tariff) {
      case 'single':
        return (
          this.tariffPricesSingle$()?.find(
            (tariffPricing) => tariffPricing.name === size
          )?.value || defaultPrice
        );
      case 'family':
        return (
          this.tariffPricesFamily$()?.find(
            (tariffPricing) => tariffPricing.name === size
          )?.value || defaultPrice
        );
      default:
        return defaultPrice;
    }
  }

  public getLink(tariff: TariffType, size: TariffSize): string {
    if (!size || !tariff) {
      return environment.insurance['none'];
    }
    return environment.insurance[tariff][size];
  }

  public getMapping(
    tariff: TariffType,
    size: TariffSize
  ): TariffMappingEntry | null {
    if (!size || !tariff) {
      return null;
    }
    return TARIFF_MAPPING[tariff][size];
  }

  selectSize(size: TariffSize | 0 | 1 | 2) {
    switch (size) {
      case 0:
        this.size = 'basic';
        break;
      case 1:
        this.size = 'comfort';
        break;
      case 2:
        this.size = 'premium';
        break;
      default:
        this.size = size;
    }
  }
}
